import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Heading,
  Text,
  Image,
  Button,
  VStack,
  HStack,
  Badge,
  Divider,
  SimpleGrid,
  useToast,
  useColorModeValue,
  Icon,
  Flex,
  Spinner,
  Grid,
  GridItem,
  IconButton,
  Tooltip
} from '@chakra-ui/react';
import {
  FaMapMarkerAlt,
  FaRupeeSign,
  FaPhone,
  FaWhatsapp,
  FaTint,
  FaCalendarAlt,
  FaShare,
  FaArrowLeft
} from 'react-icons/fa';
import axios from 'axios';
import { API_BASE_URL } from '../config/api_keys';
import Loading from '../resources/Loading';
import NotFoundComponent from '../resources/NotFound';
import Modallogin from '../Modallogin';

const AnimalDetail = ({ auth }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const [animal, setAnimal] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [contacting, setContacting] = useState(false);
  const [staticModal, setStaticModal] = useState(false);
  const [activeImageIndex, setActiveImageIndex] = useState(0);

  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const textColor = useColorModeValue('gray.700', 'gray.200');

  useEffect(() => {
    fetchAnimalDetails();
  }, [id]);

  const fetchAnimalDetails = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_BASE_URL}/api/animals/sell/${id}`);
      setAnimal(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching animal details:', error);
      setLoading(false);
      if (error.response?.status === 404) {
        setNotFound(true);
      } else {
        toast({
          title: 'Error',
          description: 'Failed to load animal details',
          status: 'error',
          duration: 3000,
          isClosable: true
        });
      }
    }
  };

  const handleContactSeller = () => {
    if (!auth) {
      setStaticModal(true);
      return;
    }

    setContacting(true);
    
    if (animal?.userphone) {
      const phoneNumber = animal.userphone.replace(/[^0-9]/g, '');
      const message = encodeURIComponent(
        `Hello, I'm interested in your ${animal.animalType} - ${animal.title} listed for ₹${animal.price?.toLocaleString()}. Is it still available?`
      );
      const whatsappUrl = `https://wa.me/${phoneNumber}?text=${message}`;
      window.open(whatsappUrl, '_blank');
    } else if (animal?.useremail) {
      // If no phone, try to open chat
      window.location.href = `/chat/${animal._id}/${animal.useremail}`;
    } else {
      toast({
        title: 'Contact Info',
        description: 'Contact information not available. Please check the listing details.',
        status: 'info',
        duration: 3000,
        isClosable: true
      });
    }
    
    setTimeout(() => setContacting(false), 1000);
  };

  const handleChat = () => {
    if (!auth) {
      setStaticModal(true);
      return;
    }

    if (animal?.useremail) {
      window.location.href = `/chat/${animal._id}/${animal.useremail}`;
    } else {
      toast({
        title: 'Chat Unavailable',
        description: 'Seller contact information not available.',
        status: 'info',
        duration: 3000,
        isClosable: true
      });
    }
  };

  const handleCallSeller = () => {
    if (!auth) {
      setStaticModal(true);
      return;
    }

    if (animal?.userphone) {
      window.location.href = `tel:${animal.userphone}`;
    } else {
      toast({
        title: 'Contact Info',
        description: 'Phone number not available',
        status: 'info',
        duration: 3000,
        isClosable: true
      });
    }
  };

  const handleShare = () => {
    if (navigator.share) {
      navigator.share({
        title: `${animal.title} - ₹${animal.price}`,
        text: `Check out this ${animal.animalType} for sale: ${animal.title}`,
        url: window.location.href
      }).catch(err => console.log('Error sharing:', err));
    } else {
      navigator.clipboard.writeText(window.location.href);
      toast({
        title: 'Link Copied',
        description: 'Animal listing link copied to clipboard',
        status: 'success',
        duration: 2000,
        isClosable: true
      });
    }
  };

  if (loading) {
    return <Loading />;
  }

  if (notFound || !animal) {
    return <NotFoundComponent />;
  }

  // Consolidate all images into a single array
  const allImages = [];
  
  // Add images from the images array
  if (animal.images && animal.images.length > 0) {
    allImages.push(...animal.images);
  }
  
  // Add udderPhotoUrl if it exists and isn't already in the images array
  if (animal.udderPhotoUrl && !allImages.includes(animal.udderPhotoUrl)) {
    allImages.push(animal.udderPhotoUrl);
  }

  return (
    <Container maxW="container.xl" py={6}>
      {/* Back Button */}
      <Button
        leftIcon={<Icon as={FaArrowLeft} />}
        variant="ghost"
        mb={4}
        onClick={() => navigate(-1)}
      >
        Back
      </Button>

      <Grid templateColumns={{ base: '1fr', lg: '2fr 1fr' }} gap={6}>
        {/* Left Column - Images and Details */}
        <GridItem>
          <VStack spacing={6} align="stretch">
            {/* Images and Videos */}
            <Box
              bg={bgColor}
              borderRadius="lg"
              borderWidth="1px"
              borderColor={borderColor}
              overflow="hidden"
            >
              {allImages.length > 0 ? (
                <Box position="relative">
                  <Image
                    src={allImages[activeImageIndex]}
                    alt={animal.title}
                    w="100%"
                    h="400px"
                    objectFit="cover"
                  />
                  {allImages.length > 1 && (
                    <HStack
                      position="absolute"
                      bottom={4}
                      left="50%"
                      transform="translateX(-50%)"
                      spacing={2}
                    >
                      {allImages.map((_, index) => (
                        <Box
                          key={index}
                          w={activeImageIndex === index ? '12px' : '8px'}
                          h="8px"
                          borderRadius="full"
                          bg={activeImageIndex === index ? 'white' : 'whiteAlpha.600'}
                          cursor="pointer"
                          onClick={() => setActiveImageIndex(index)}
                        />
                      ))}
                    </HStack>
                  )}
                </Box>
              ) : animal.videoUrl ? (
                <Box w="100%" h="400px">
                  <video
                    src={animal.videoUrl}
                    controls
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'contain',
                      backgroundColor: '#000'
                    }}
                  >
                    Your browser does not support the video tag.
                  </video>
                </Box>
              ) : (
                <Box
                  h="400px"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  bg="gray.100"
                >
                  <Text color="gray.500">No Image Available</Text>
                </Box>
              )}

              {/* Thumbnail Gallery */}
              {(allImages.length > 1 || (animal.videoUrl && allImages.length > 0)) && (
                <HStack p={4} spacing={2} overflowX="auto">
                  {allImages.map((img, index) => (
                    <Image
                      key={index}
                      src={img}
                      alt={`${animal.title} ${index + 1}`}
                      w="80px"
                      h="80px"
                      objectFit="cover"
                      borderRadius="md"
                      cursor="pointer"
                      borderWidth={activeImageIndex === index ? '2px' : '1px'}
                      borderColor={activeImageIndex === index ? 'green.500' : 'gray.200'}
                      onClick={() => setActiveImageIndex(index)}
                    />
                  ))}
                </HStack>
              )}
            </Box>

            {/* Videos Section */}
            {(animal.videoUrl || animal.milkingVideoUrl) && (
              <Box bg={bgColor} p={6} borderRadius="lg" borderWidth="1px" borderColor={borderColor}>
                <Heading size="md" mb={4}>Videos</Heading>
                <VStack spacing={4} align="stretch">
                  {animal.videoUrl && (
                    <Box>
                      <Text fontWeight="bold" mb={2}>Animal Video</Text>
                      <Box w="100%" h="300px" borderRadius="md" overflow="hidden">
                        <video
                          src={animal.videoUrl}
                          controls
                          style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'contain',
                            backgroundColor: '#000'
                          }}
                        >
                          Your browser does not support the video tag.
                        </video>
                      </Box>
                    </Box>
                  )}
                  
                  {animal.milkingVideoUrl && (
                    <Box>
                      <Text fontWeight="bold" mb={2}>Milking Video</Text>
                      <Box w="100%" h="300px" borderRadius="md" overflow="hidden">
                        <video
                          src={animal.milkingVideoUrl}
                          controls
                          style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'contain',
                            backgroundColor: '#000'
                          }}
                        >
                          Your browser does not support the video tag.
                        </video>
                      </Box>
                    </Box>
                  )}
                </VStack>
              </Box>
            )}

            {/* Animal Details */}
            <Box bg={bgColor} p={6} borderRadius="lg" borderWidth="1px" borderColor={borderColor}>
              <VStack align="stretch" spacing={4}>
                <HStack justify="space-between" align="start">
                  <VStack align="start" spacing={2}>
                    <Heading size="lg">{animal.title}</Heading>
                    <HStack>
                      <Badge colorScheme="green" fontSize="md" px={3} py={1}>
                        {animal.animalType}
                      </Badge>
                      {animal.lactationCycle && (
                        <Badge colorScheme="blue" fontSize="md" px={3} py={1}>
                          {animal.lactationCycle === 'not_calved' ? 'Not Calved' :
                           animal.lactationCycle === 'first' ? 'First Lactation' :
                           animal.lactationCycle === 'second' ? 'Second Lactation' :
                           'Other Lactation'}
                        </Badge>
                      )}
                    </HStack>
                  </VStack>
                  <IconButton
                    icon={<FaShare />}
                    aria-label="Share"
                    onClick={handleShare}
                    variant="ghost"
                  />
                </HStack>

                <Divider />

                <Text fontSize="md" color={textColor}>
                  {animal.description}
                </Text>

                {/* Key Information */}
                <SimpleGrid columns={2} spacing={4} mt={4}>
                  {animal.currentMilkPerDay && (
                    <Box>
                      <HStack>
                        <Icon as={FaTint} color="blue.500" />
                        <Text fontWeight="bold">Milk Production</Text>
                      </HStack>
                      <Text fontSize="lg" color="blue.600" mt={1}>
                        {animal.currentMilkPerDay} L/day
                      </Text>
                    </Box>
                  )}

                  {animal.price && (
                    <Box>
                      <HStack>
                        <Icon as={FaRupeeSign} color="green.500" />
                        <Text fontWeight="bold">Price</Text>
                      </HStack>
                      <Text fontSize="2xl" fontWeight="bold" color="green.600" mt={1}>
                        ₹{animal.price.toLocaleString()}
                      </Text>
                    </Box>
                  )}

                  {animal.address && (
                    <Box>
                      <HStack>
                        <Icon as={FaMapMarkerAlt} color="red.500" />
                        <Text fontWeight="bold">Location</Text>
                      </HStack>
                      <Text fontSize="md" color={textColor} mt={1}>
                        {animal.address.fullAddress || 
                         `${animal.address.area || ''}, ${animal.address.city}, ${animal.address.state}`.trim()}
                      </Text>
                    </Box>
                  )}

                  {animal.sellingTimeline && (
                    <Box>
                      <HStack>
                        <Icon as={FaCalendarAlt} color="orange.500" />
                        <Text fontWeight="bold">Selling Timeline</Text>
                      </HStack>
                      <Text fontSize="md" color={textColor} mt={1}>
                        {animal.sellingTimeline}
                      </Text>
                    </Box>
                  )}
                </SimpleGrid>

                {/* Additional Details */}
                {animal.animalDetails && Object.keys(animal.animalDetails).length > 0 && (
                  <>
                    <Divider />
                    <Heading size="md">Additional Details</Heading>
                    <SimpleGrid columns={2} spacing={4}>
                      {animal.animalDetails.age && (
                        <Box>
                          <Text fontSize="sm" color="gray.500">Age</Text>
                          <Text fontSize="md">{animal.animalDetails.age}</Text>
                        </Box>
                      )}
                      {animal.animalDetails.breed && (
                        <Box>
                          <Text fontSize="sm" color="gray.500">Breed</Text>
                          <Text fontSize="md">{animal.animalDetails.breed}</Text>
                        </Box>
                      )}
                      {animal.animalDetails.weight && (
                        <Box>
                          <Text fontSize="sm" color="gray.500">Weight</Text>
                          <Text fontSize="md">
                            {animal.animalDetails.weight} {animal.animalDetails.weightUnit || 'kg'}
                          </Text>
                        </Box>
                      )}
                    </SimpleGrid>
                  </>
                )}

                {animal.additionalInfo && (
                  <>
                    <Divider />
                    <Box>
                      <Text fontWeight="bold" mb={2}>Additional Information</Text>
                      <Text fontSize="md" color={textColor}>{animal.additionalInfo}</Text>
                    </Box>
                  </>
                )}
              </VStack>
            </Box>
          </VStack>
        </GridItem>

        {/* Right Column - Seller Info and Actions */}
        <GridItem>
          <VStack spacing={4} align="stretch" position={{ lg: 'sticky' }} top={4}>
            {/* Price Card */}
            <Box bg={bgColor} p={6} borderRadius="lg" borderWidth="1px" borderColor={borderColor}>
              <VStack spacing={4}>
                <Text fontSize="3xl" fontWeight="bold" color="green.600">
                  ₹{animal.price?.toLocaleString()}
                </Text>
                <Text fontSize="sm" color="gray.500">
                  {animal.animalSource === 'tethered' ? 'Tethered Animal' : 'Market Animal'}
                </Text>
              </VStack>
            </Box>

            {/* Seller Info */}
            <Box bg={bgColor} p={6} borderRadius="lg" borderWidth="1px" borderColor={borderColor}>
              <VStack align="stretch" spacing={4}>
                <Heading size="md">Seller Information</Heading>
                <Divider />
                {animal.username && (
                  <Box>
                    <Text fontSize="sm" color="gray.500">Name</Text>
                    <Text fontSize="md" fontWeight="medium">{animal.username}</Text>
                  </Box>
                )}
                {animal.address && (
                  <Box>
                    <HStack>
                      <Icon as={FaMapMarkerAlt} color="red.500" />
                      <VStack align="start" spacing={0}>
                        <Text fontSize="sm" color="gray.500">Location</Text>
                        <Text fontSize="md">
                          {animal.address.city}, {animal.address.state}
                        </Text>
                      </VStack>
                    </HStack>
                  </Box>
                )}
              </VStack>
            </Box>

            {/* Action Buttons */}
            <VStack spacing={3}>
              <Button
                colorScheme="green"
                size="lg"
                w="100%"
                leftIcon={<Icon as={FaWhatsapp} />}
                onClick={handleContactSeller}
                isLoading={contacting}
              >
                {animal.userphone ? 'Contact via WhatsApp' : 'Contact Seller'}
              </Button>

              {animal.userphone && (
                <Button
                  colorScheme="blue"
                  size="lg"
                  w="100%"
                  leftIcon={<Icon as={FaPhone} />}
                  onClick={handleCallSeller}
                >
                  Call Seller
                </Button>
              )}

              {auth && animal.useremail && (
                <Button
                  colorScheme="purple"
                  size="lg"
                  w="100%"
                  onClick={handleChat}
                >
                  Start Chat
                </Button>
              )}

              <Button
                variant="outline"
                size="lg"
                w="100%"
                onClick={() => navigate('/livestock')}
              >
                Browse More Animals
              </Button>
            </VStack>

            {/* Safety Tips */}
            <Box bg="yellow.50" p={4} borderRadius="md" borderWidth="1px" borderColor="yellow.200">
              <Text fontSize="sm" fontWeight="bold" mb={2}>Safety Tips</Text>
              <VStack align="start" spacing={1} fontSize="xs" color="gray.700">
                <Text>• Meet in a public place</Text>
                <Text>• Verify animal health before purchase</Text>
                <Text>• Get proper documentation</Text>
                <Text>• Use secure payment methods</Text>
              </VStack>
            </Box>
          </VStack>
        </GridItem>
      </Grid>

      {/* Login Modal */}
      {staticModal && (
        <Modallogin
          setStaticModal={setStaticModal}
          toggleShow={() => setStaticModal(false)}
          staticModal={staticModal}
        />
      )}
    </Container>
  );
};

export default AnimalDetail;